import React, { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { atoms } from '@fiverr-private/theme';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Icon } from '@fiverr-private/fit';
import { Image } from '@fiverr-private/media';
import { CompanyPlaceholderIcon } from '@fiverr-private/icons';
import { WorkExperience as WorkExperienceType } from '../../../../gql-operations/gql-generated';
import { useSellerData } from '../../../../hooks/useSellerData';
import { FederatedSeller } from '../../../../types';
import { CurrentViewState } from '../../shared/types';
import CompanyTitle from '../../subcomponents/CompanyTitle';
import ExperienceDetails from '../../subcomponents/ExperienceDetails';
import { getWorkExperienceAmount } from '../../utils';
import { TimelineContainer, TimelineItem } from '../Timeline';

import { placeholderCompanyLogo } from '../../shared/styles.ve.css';

interface WorkExperiencesProps {
    showAll?: boolean;
    setCurrentViewState?: Dispatch<SetStateAction<CurrentViewState>>;
    workExperiences?: WorkExperienceType[] | null;
    isCard?: boolean;
}

const companyLogo = atoms({
    borderRadius: 'sm',
});

const addExperienceToCompanyArray = ({
    companyArray,
    experience,
    index,
    experiences,
}: {
    companyArray: WorkExperienceType[][];
    experience: WorkExperienceType;
    index: number;
    experiences: WorkExperienceType[];
}) => {
    if (index === 0) {
        companyArray.push([experience]);
    } else if (experience.company.name.toLowerCase() === experiences[index - 1].company.name.toLowerCase()) {
        companyArray[companyArray.length - 1].push(experience);
    } else {
        companyArray.push([experience]);
    }
};

const WorkExperienceList: FC<WorkExperiencesProps> = ({ showAll, setCurrentViewState, workExperiences, isCard }) => {
    const { workExperiences: workExperiencesFromSellerData } = useSellerData(selectSellerSlice);
    const actualWorkExperiences = workExperiences || workExperiencesFromSellerData;
    const workExperienceAmount = getWorkExperienceAmount(actualWorkExperiences);

    const { companies, visibleCompanies } = useMemo(() => {
        const visibleCompanies: WorkExperienceType[][] = [];
        const companies: WorkExperienceType[][] = [];
        actualWorkExperiences?.forEach((item: WorkExperienceType, index: number) => {
            addExperienceToCompanyArray({
                companyArray: companies,
                experience: item,
                index,
                experiences: actualWorkExperiences,
            });

            if (index < workExperienceAmount) {
                addExperienceToCompanyArray({
                    companyArray: visibleCompanies,
                    experience: item,
                    index,
                    experiences: actualWorkExperiences,
                });
            }
        });

        return { companies, visibleCompanies };
    }, [actualWorkExperiences, workExperienceAmount]);

    const companiesToIterate = showAll ? companies : visibleCompanies;

    return (
        <Container width="100%">
            {companiesToIterate?.map((companyExperiences, index) => {
                const isTimeline = companyExperiences.length > 1;
                const companyName = companyExperiences[0].company.name;

                return companiesToIterate[index] ? (
                    <Stack
                        key={index}
                        gap="3"
                        marginBottom="6"
                        {...(isCard && {
                            paddingY: { default: '4' },
                            paddingX: { default: '3' },
                            borderWidth: 'sm',
                            borderColor: { default: 'grey_400' },
                            borderRadius: 'sm',
                        })}
                    >
                        <Container className="text-bold" maxWidth={40} minWidth={40} marginY="1">
                            {companyExperiences[0]?.company?.logo ? (
                                <Image
                                    className={companyLogo}
                                    alt={companyName}
                                    src={companyExperiences[0]?.company.logo}
                                />
                            ) : (
                                <Icon className={placeholderCompanyLogo} size={38} ariaLabel={companyName}>
                                    <CompanyPlaceholderIcon />
                                </Icon>
                            )}
                        </Container>
                        <Container width="100%" maxWidth="100%" direction="column">
                            {isTimeline && (
                                <CompanyTitle companyName={companyName} companyExperiences={companies[index]} />
                            )}
                            {isTimeline ? (
                                <TimelineContainer>
                                    {companiesToIterate[index].map((experience) => (
                                        <TimelineItem key={experience.id}>
                                            <ExperienceDetails
                                                isCard={isCard}
                                                experience={experience}
                                                inTimeline
                                                index={index}
                                                setCurrentViewState={setCurrentViewState}
                                            />
                                        </TimelineItem>
                                    ))}
                                </TimelineContainer>
                            ) : (
                                <ExperienceDetails
                                    isCard={isCard}
                                    experience={companyExperiences[0]}
                                    index={index}
                                    setCurrentViewState={setCurrentViewState}
                                />
                            )}
                        </Container>
                    </Stack>
                ) : null;
            })}
        </Container>
    );
};

function selectSellerSlice(seller: FederatedSeller) {
    return {
        workExperiences: seller.workExperiences.nodes,
    };
}

export default WorkExperienceList;
